import { RollbackOutlined } from '@ant-design/icons';
import { Col, Dropdown, Image, Row, Tag, Tooltip } from 'antd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Routes } from '../../../../app/routes';
import CustomButton from '../../../../shared/components/buttons/custom-button/custom-button';
import CollapsablePreview from '../../../../shared/components/collapsable-preview/collapsable-preview';
import PageSpinner from '../../../../shared/components/page-spinner/page-spinner';
import { useBreakpoint } from '../../../../shared/hooks/use-breakpoint';
import { useHasPermission } from '../../../../shared/hooks/use-has-permission';
import { ArchiveIcon } from '../../../../shared/icons/archive-icon';
import { MoreIcon } from '../../../../shared/icons/more-icon';
import RequestPreviewContentData from '../../../request/components/request-preview-content-data/request-preview-content-data';
import { ArrowUpRightIcon } from '../../../../shared/icons/arrow-up-right-icon';
import { BoxArrowUpRightIcon } from '../../../../shared/icons/box-arrow-up-right-icon';
import { ClockHistoryIcon } from '../../../../shared/icons/clock-history-icon';
import { TrashIcon } from '../../../../shared/icons/trash-icon';
import { VectorIcon } from '../../../../shared/icons/vector-icon';
import { convertServerDateTimeString } from '../../../../shared/utils/format-date/format-date';
import { useAppSelector } from '../../../../store/hooks';
import { RequestDetails } from '../../../request/models/request-details';
import { openModal } from '../../../ui/store/ui-slice';
import { UserPermissionType } from '../../../user/models/user-permission-type';
import {
  scanRequestArchiveModalId,
  scanRequestDeleteModalId,
  scanRequestDetachModalId,
  scanRequestMoveModalId,
  scanRequestRestoreModalId,
  scanRequestUpdateModalId,
} from '../../constants/constants';
import {
  selectActiveScanRequestId,
  selectTempScanRequestId,
  setTempScanRequestId,
} from '../../store/scan-requests-slice';
import { getScanRequestFileUrl } from '../../utils/get-scan-request-file-url';
import ScanRequestArchiveModal from '../scan-request-archive-modal/scan-request-archive-modal';
import ScanRequestUpdateFormModal from '../scan-request-create-form-modal/scan-request-update-form-modal';
import ScanRequestDeleteModal from '../scan-request-delete-modal/scan-request-delete-modal';
import ScanRequestFormDetachModal from '../scan-request-form-detach-modal/scan-request-form-detach-modal';
import ScanRequestMoveFormModal from '../scan-request-move-form-modal/scan-request-move-form-modal';
import ScanRequestRestoreModal from '../scan-request-restore-modal/scan-request-restore-modal';
import styles from './scan-requests-preview.module.css';
import { useGetUserSettingsQuery } from '../../../user/api/user-api';
import { UserSettings } from '../../../user/models/user-settings';
import { hasPermission } from '../../../../shared/utils/has-permission';
import { User } from '../../../user/models/user';

interface Props {
  request: RequestDetails;
  currentUser?: User;
}

const ScanRequestsPreview = ({ request, currentUser }: Props): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['common', 'scan-requests']);
  const activeScanRequestId = useAppSelector(selectActiveScanRequestId);
  const activeTempScanRequestId = useAppSelector(selectTempScanRequestId);
  const hasPermissionArchiveDocuments = useHasPermission(UserPermissionType.ArchiveDocuments);
  const screens = useBreakpoint();
  const { data: userSettings } = useGetUserSettingsQuery();

  const openFormModal = (modalId: string, scanRequestId: string) => {
    dispatch(openModal(modalId));
    dispatch(setTempScanRequestId(scanRequestId));
  };

  return (
    <>
      {request?.scanRequests.map(scanRequest => (
        <CollapsablePreview
          title={`${
            scanRequest.name || t('scanRequestsTablePreviewContentCollapsible.scannedDocument', { ns: 'scan-requests' })
          } - ${convertServerDateTimeString(scanRequest.scannedAt, userSettings?.[UserSettings.DateFormat])}`}
          index={scanRequest.id}
          key={scanRequest.id}
          icon={<VectorIcon />}
          defaultActiveKey={activeTempScanRequestId || activeScanRequestId || request?.scanRequests[0].id}
        >
          <>
            {!screens.xs && (
              <RequestPreviewContentData
                contentData={[
                  {
                    title: t('label.document'),
                    data: scanRequest.name,
                    onEdit: () => openFormModal(scanRequestUpdateModalId, scanRequest.id),
                  },
                  {
                    title: t('scanRequestsTablePreviewContentCollapsible.blocks.documentStatus', {
                      ns: 'scan-requests',
                    }),
                    data: (
                      <Tag>
                        {scanRequest.status === 'scanned'
                          ? t('statusOption.scanned', { ns: 'scan-requests' })
                          : scanRequest.status === 'pending' && t('signScanRequest.label.statusOption.pending')}
                      </Tag>
                    ),
                  },
                  {
                    title: t('signScanRequest.label.scannedAt'),
                    data: convertServerDateTimeString(scanRequest.scannedAt, userSettings?.[UserSettings.DateFormat]),
                  },
                  {
                    title: t('label.user'),
                    data: scanRequest.clientUser?.name || '',
                  },
                  ...(currentUser && hasPermission(currentUser, UserPermissionType.ViewCustomers)
                    ? [
                        {
                          title: t('label.customer'),
                          data: scanRequest.customer?.name || '',
                        },
                      ]
                    : []),
                ]}
              />
            )}
            <Row className={clsx(styles.actionButtonRow)} justify="start" gutter={[10, 10]}>
              <Col>
                <a
                  href={getScanRequestFileUrl(scanRequest.id, true)}
                  className={clsx({ [styles.downloadActionDisabled]: scanRequest.deleted })}
                  download
                >
                  <CustomButton
                    size="small"
                    type="primaryLight"
                    fontWeight={400}
                    fontSize={12}
                    disabled={scanRequest.deleted}
                  >
                    {t('action.download')}
                  </CustomButton>
                </a>
              </Col>
              {!screens.xs && (
                <>
                  {scanRequest.deleted && (
                    <Col>
                      <CustomButton
                        size="small"
                        type="textBg"
                        fontWeight={400}
                        fontSize={12}
                        icon={<RollbackOutlined />}
                        onClick={() => openFormModal(scanRequestRestoreModalId, scanRequest.id)}
                      >
                        {t('signScanRequest.action.restore')}
                      </CustomButton>
                    </Col>
                  )}
                  <Col className={styles.dropdownColumn}>
                    <Dropdown
                      arrow
                      placement="bottomRight"
                      classNames={{ root: styles.actionsDropdown }}
                      trigger={['hover', 'click']}
                      menu={{
                        items: [
                          {
                            key: 'history',
                            label: (
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              <Link to={Routes.ScanRequestTraceLogs.replace(':id', activeScanRequestId!)}>
                                <CustomButton
                                  size="small"
                                  fontWeight={400}
                                  fontSize={12}
                                  type="textBg"
                                  icon={<ClockHistoryIcon />}
                                >
                                  {t('action.history')}
                                </CustomButton>
                              </Link>
                            ),
                          },
                          {
                            key: 'detach',
                            label: (
                              <CustomButton
                                size="small"
                                type="textBg"
                                fontWeight={400}
                                fontSize={12}
                                icon={<BoxArrowUpRightIcon />}
                                onClick={() => openFormModal(scanRequestDetachModalId, scanRequest.id)}
                                disabled={
                                  !(
                                    request.signRequests.length > 1 ||
                                    request.scanRequests.length > 1 ||
                                    (request.signRequests.length > 0 && request.scanRequests.length > 0)
                                  ) || scanRequest.deleted
                                }
                              >
                                {t('signScanRequest.action.detach')}
                              </CustomButton>
                            ),
                          },
                          {
                            key: 'move',
                            label: (
                              <CustomButton
                                size="small"
                                type="textBg"
                                fontWeight={400}
                                fontSize={12}
                                icon={<ArrowUpRightIcon />}
                                onClick={() => openFormModal(scanRequestMoveModalId, scanRequest.id)}
                                disabled={scanRequest.deleted}
                              >
                                {t('signScanRequest.action.move')}
                              </CustomButton>
                            ),
                          },
                          ...(scanRequest.canArchive
                            ? [
                                {
                                  key: 'uploadToServer',
                                  label: (
                                    <CustomButton
                                      size="small"
                                      type="textBg"
                                      fontWeight={400}
                                      fontSize={12}
                                      icon={<ArchiveIcon />}
                                      onClick={() => openFormModal(scanRequestArchiveModalId, scanRequest.id)}
                                      disabled={!hasPermissionArchiveDocuments}
                                    >
                                      {t('signScanRequest.action.uploadToServer')}
                                    </CustomButton>
                                  ),
                                },
                              ]
                            : []),
                          ...(!scanRequest.deleted
                            ? [
                                {
                                  key: 'delete',
                                  label: (
                                    <Tooltip
                                      mouseEnterDelay={0.5}
                                      title={
                                        currentUser &&
                                        !hasPermission(currentUser, UserPermissionType.DeleteDocuments) &&
                                        t('hint.noPermissionDeleteDocument')
                                      }
                                    >
                                      <CustomButton
                                        size="small"
                                        danger
                                        type="default"
                                        fontWeight={400}
                                        fontSize={12}
                                        icon={<TrashIcon />}
                                        disabled={
                                          currentUser && !hasPermission(currentUser, UserPermissionType.DeleteDocuments)
                                        }
                                        onClick={() => openFormModal(scanRequestDeleteModalId, scanRequest.id)}
                                      >
                                        {t('action.delete')}
                                      </CustomButton>
                                    </Tooltip>
                                  ),
                                },
                              ]
                            : []),
                        ],
                      }}
                    >
                      <CustomButton size="small" icon={<MoreIcon />} className="btn-only-icon" type="secondaryLight" />
                    </Dropdown>
                  </Col>
                </>
              )}
            </Row>
            <div className={styles.scanImageWrapper}>
              <Image placeholder={<PageSpinner />} alt={scanRequest.name} src={getScanRequestFileUrl(scanRequest.id)} />
            </div>
          </>
        </CollapsablePreview>
      ))}
      <ScanRequestMoveFormModal />
      <ScanRequestFormDetachModal />
      <ScanRequestDeleteModal />
      <ScanRequestRestoreModal />
      <ScanRequestArchiveModal />
      <ScanRequestUpdateFormModal
        formId={scanRequestUpdateModalId}
        inputsData={{
          name: {
            initialValue: request.scanRequests.find(scanRequest => scanRequest.id === activeScanRequestId)?.name || '',
            label: t('signScanRequest.label.documentName'),
            placeholder: t('signScanRequest.label.documentName'),
          },
        }}
      />
    </>
  );
};

export default ScanRequestsPreview;
